import { Spinner2 } from 'components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL;

const Frame = styled.iframe`
  display: block;
  width: 100%;
  height: 100vh;
  border: 0;
  background: #fff;
`;

const Centered = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  text-align: center;
`;

/**
 * Links inside the email open in this tab, not in the frame and not in a new tab.
 *
 * MJML marks every anchor `target="_blank"`, which from a frame opens a second tab per tap; an
 * anchor with no target would load its destination into the frame, where the app handoff cannot
 * run. `_top` is the page a fan pressed "View in browser" to reach, replaced by where they tapped.
 */
function withTopTarget(html) {
  const base = '<base target="_top">',
    retargeted = html.replace(/target="_blank"/gi, 'target="_top"');

  return /<head[^>]*>/i.test(retargeted)
    ? retargeted.replace(/<head[^>]*>/i, (head) => `${head}${base}`)
    : `${base}${retargeted}`;
}

/**
 * The "View in browser" page.
 *
 * The email's footer link used to be the API endpoint that renders the email, so a fan landed on
 * an address under `/api/public/…`. The link now comes here, and this page asks that endpoint for
 * the same HTML and shows it inside the site.
 *
 * Framed rather than injected, because the email is a complete document with its own `<style>`
 * and would restyle this app if it were written into the page. Sandboxed with no scripts: the
 * email has none, and the frame needs only to let a tapped link navigate the tab.
 *
 * The endpoint's error pages are HTML too ("expired", "no longer available"), so whatever it
 * answers is shown, whatever the status.
 */
const EmailView = () => {
  const [params] = useSearchParams(),
    token = params.get('token'),
    variant = params.get('variant'),
    [html, setHtml] = useState(null),
    [failed, setFailed] = useState(false),
    { t } = useTranslation();

  useEffect(() => {
    if (!token) {
      setFailed(true);
      return;
    }

    const query = new URLSearchParams({ token });
    if (variant) query.set('variant', variant);

    let cancelled = false;

    fetch(`${API_URL}/public/email/view?${query}`)
      .then((response) => response.text())
      .then((body) => !cancelled && setHtml(body))
      .catch(() => !cancelled && setFailed(true));

    return () => {
      cancelled = true;
    };
  }, [token, variant]);

  if (failed) return <Centered>{t('emailView.unavailable')}</Centered>;

  if (html === null) {
    return (
      <Centered>
        <Spinner2 />
      </Centered>
    );
  }

  return (
    <Frame
      title={t('emailView.title')}
      srcDoc={withTopTarget(html)}
      sandbox="allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
    />
  );
};

export default EmailView;
